/**
 * src/components/ui/Avatar.jsx — initials avatar for an artist or client.
 *
 * No photo upload exists anywhere in this product today (checked: neither
 * `artists` nor `clients` has an image column — see migrations/), so this
 * is initials-on-a-circle only, not an `<img>`-with-fallback component. Per
 * direction.md's Stencil principle, radius is fully round here — the one
 * place roundness is deliberate, contrasting with the near-zero radius
 * everywhere else ("drawn" is sharp, "person" is round").
 */

/**
 * @param {string} name
 * @returns {string} Up to two initials, e.g. "Maya Okafor" → "MO".
 */
function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/**
 * @param {{ name: string, size?: 'sm'|'md', decorative?: boolean }} props
 *   `decorative` — set true only when the full name is already visible
 *   immediately next to the avatar (e.g. a table row with its own name
 *   column), so the initials aren't announced twice.
 * @returns {JSX.Element}
 */
export function Avatar({ name, size = 'md', decorative = false }) {
  return (
    <span
      className={`ui-avatar ui-avatar--${size}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
      title={name}
    >
      {initials(name)}
    </span>
  );
}
